import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel, GridRowId } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Add,
  Home as HomeIcon,
  Delete as DeleteIcon,
  AutoAwesome as EmbeddingIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading as setColumnsLoading,
  setError as setColumnsError,
  setColumns,
  setSelectedColumn,
  setPage as setColumnsPage,
  setPageSize as setColumnsPageSize,
  setSearch as setColumnsSearch,
  clearSelectedColumn,
  addColumn,
  updateColumn as updateColumnAction,
  deleteColumn as deleteColumnAction,
  resetColumns,
} from '../../store/slices/columnsSlice';
import {
  setLoading as setRelLoading,
  setError as setRelError,
  setRelationships,
  setSelectedRelationship,
  setPage as setRelPage,
  setPageSize as setRelPageSize,
  setSearch as setRelSearch,
  clearSelectedRelationship,
  addRelationship as addRelationshipAction,
  updateRelationship as updateRelationshipAction,
  deleteRelationship as deleteRelationshipAction,
  resetRelationships,
} from '../../store/slices/relationshipsSlice';
import { GET_COLUMNS_LIST, GET_COLUMN } from '../../graphql/queries/columns.queries';
import { GET_TABLE, GET_TABLES_LIST } from '../../graphql/queries/tables.queries';
import { GET_RELATIONSHIPS_LIST, GET_RELATIONSHIP } from '../../graphql/queries/relationships.queries';
import {
  CREATE_COLUMN,
  UPDATE_COLUMN,
  DELETE_COLUMN,
  CREATE_COLUMN_EMBEDDING,
} from '../../graphql/mutations/columns.mutations';
import { CREATE_RELATIONSHIP, UPDATE_RELATIONSHIP, DELETE_RELATIONSHIP } from '../../graphql/mutations/relationships.mutations';
import {
  FindColumnListInput,
  FindColumnInput,
  CreateColumnInput,
  UpdateColumnInput,
  DeleteColumnInput,
  CreateColumnResponse,
  UpdateColumnResponse,
  DeleteColumnResponse,
  ColumnsListResponse,
  ColumnResponse,
  Column,
  CreateColumnEmbeddingInput,
  CreateColumnEmbeddingResponse,
} from '../../types/columns.types';
import {
  TableResponse,
  FindTableInput,
  Table,
  TablesListResponse,
  FindTableListInput,
} from '../../types/tables.types';
import {
  FindRelationshipListInput,
  FindRelationshipInput,
  RelationshipsListResponse,
  RelationshipResponse,
  Relationship,
  CreateRelationshipInput,
  CreateRelationshipResponse,
  UpdateRelationshipInput,
  UpdateRelationshipResponse,
  DeleteRelationshipInput,
  DeleteRelationshipResponse,
} from '../../types/relationships.types';
import ViewColumnModal from '../../components/Columns/ViewColumnModal';
import CreateEditColumnModal, {
  ColumnFormData,
} from '../../components/Columns/CreateEditColumnModal';
import ConfirmDeleteColumnDialog from '../../components/Columns/ConfirmDeleteColumnDialog';
import ConfirmCreateColumnEmbeddingDialog from '../../components/Columns/ConfirmCreateColumnEmbeddingDialog';
import ViewRelationshipModal from '../../components/Relationships/ViewRelationshipModal';
import CreateEditRelationshipModal, {
  RelationshipFormData,
} from '../../components/Relationships/CreateEditRelationshipModal';
import ConfirmDeleteRelationshipDialog from '../../components/Relationships/ConfirmDeleteRelationshipDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './TableConfiguration.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`table-config-tabpanel-${index}`}
      aria-labelledby={`table-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TableConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { tableId } = useParams<{ tableId: string }>();
  const dispatch = useAppDispatch();

  const { columns, loading: colLoading, error: colError, pagination: colPagination, search: colSearch } =
    useAppSelector((state) => state.columns);
  const {
    relationships,
    loading: relLoading,
    error: relError,
    pagination: relPagination,
    search: relSearch,
  } = useAppSelector((state) => state.relationships);

  // General state
  const [tabValue, setTabValue] = useState(0);
  const [tableInfo, setTableInfo] = useState<Table | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // === Columns tab state ===
  const [colSearchInput, setColSearchInput] = useState(colSearch);
  const [viewColModalOpen, setViewColModalOpen] = useState(false);
  const [createEditColModalOpen, setCreateEditColModalOpen] = useState(false);
  const [confirmDeleteColOpen, setConfirmDeleteColOpen] = useState(false);
  const [confirmEmbeddingOpen, setConfirmEmbeddingOpen] = useState(false);
  const [columnToDelete, setColumnToDelete] = useState<Column | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<GridRowId>() });
  const [colModalMode, setColModalMode] = useState<'create' | 'edit'>('create');
  const [colSaving, setColSaving] = useState(false);
  const [colDeleting, setColDeleting] = useState(false);
  const [generatingEmbedding, setGeneratingEmbedding] = useState(false);
  const [colSaveError, setColSaveError] = useState<string | null>(null);
  const [colDeleteError, setColDeleteError] = useState<string | null>(null);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);

  // === Relationships tab state ===
  const [relSearchInput, setRelSearchInput] = useState(relSearch);
  const [viewRelModalOpen, setViewRelModalOpen] = useState(false);
  const [createEditRelModalOpen, setCreateEditRelModalOpen] = useState(false);
  const [confirmDeleteRelOpen, setConfirmDeleteRelOpen] = useState(false);
  const [relationshipToDelete, setRelationshipToDelete] = useState<Relationship | null>(null);
  const [relModalMode, setRelModalMode] = useState<'create' | 'edit'>('create');
  const [relSaving, setRelSaving] = useState(false);
  const [relDeleting, setRelDeleting] = useState(false);
  const [relSaveError, setRelSaveError] = useState<string | null>(null);
  const [relDeleteError, setRelDeleteError] = useState<string | null>(null);
  const [availableTables, setAvailableTables] = useState<Table[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);

  // === Queries & Mutations - Columns ===
  const [fetchColumns, { loading: fetchingColList }] = useLazyQuery<
    { columns: ColumnsListResponse },
    { params: FindColumnListInput }
  >(GET_COLUMNS_LIST, { fetchPolicy: 'network-only' });

  const [fetchColumn] = useLazyQuery<
    { column: ColumnResponse },
    { params: FindColumnInput }
  >(GET_COLUMN, { fetchPolicy: 'network-only' });

  const [fetchTable] = useLazyQuery<
    { table: TableResponse },
    { params: FindTableInput }
  >(GET_TABLE, { fetchPolicy: 'network-only' });

  const [fetchTablesList] = useLazyQuery<
    { tables: TablesListResponse },
    { params: FindTableListInput }
  >(GET_TABLES_LIST, { fetchPolicy: 'network-only' });

  const [createColumnMutation] = useMutation<
    { createColumn: CreateColumnResponse },
    { params: CreateColumnInput }
  >(CREATE_COLUMN);

  const [updateColumnMutation] = useMutation<
    { updateColumn: UpdateColumnResponse },
    { params: UpdateColumnInput }
  >(UPDATE_COLUMN);

  const [deleteColumnMutation] = useMutation<
    { deleteColumn: DeleteColumnResponse },
    { params: DeleteColumnInput }
  >(DELETE_COLUMN);

  const [createColumnEmbeddingMutation] = useMutation<
    { createColumnEmbedding: CreateColumnEmbeddingResponse },
    { params: CreateColumnEmbeddingInput }
  >(CREATE_COLUMN_EMBEDDING);

  // === Queries & Mutations - Relationships ===
  const [fetchRelationships, { loading: fetchingRelList }] = useLazyQuery<
    { tableRelationships: RelationshipsListResponse },
    { params: FindRelationshipListInput }
  >(GET_RELATIONSHIPS_LIST, { fetchPolicy: 'network-only' });

  const [fetchRelationship] = useLazyQuery<
    { tableRelationship: RelationshipResponse },
    { params: FindRelationshipInput }
  >(GET_RELATIONSHIP, { fetchPolicy: 'network-only' });

  const [createRelationshipMutation] = useMutation<
    { createTableRelationship: CreateRelationshipResponse },
    { params: CreateRelationshipInput }
  >(CREATE_RELATIONSHIP);

  const [updateRelationshipMutation] = useMutation<
    { updateTableRelationship: UpdateRelationshipResponse },
    { params: UpdateRelationshipInput }
  >(UPDATE_RELATIONSHIP);

  const [deleteRelationshipMutation] = useMutation<
    { deleteTableRelationship: DeleteRelationshipResponse },
    { params: DeleteRelationshipInput }
  >(DELETE_RELATIONSHIP);

  // === Effects ===
  useEffect(() => {
    if (tableId) {
      loadTableInfo();
    }
    return () => {
      dispatch(resetColumns());
      dispatch(resetRelationships());
    };
  }, [tableId]);

  useEffect(() => {
    if (tableId) {
      loadColumns();
    }
  }, [tableId, colPagination.page, colPagination.pageSize, colSearch]);

  useEffect(() => {
    if (tableId) {
      loadRelationships();
    }
  }, [tableId, relPagination.page, relPagination.pageSize, relSearch]);

  // === Loaders ===
  const loadTableInfo = async () => {
    if (!tableId) return;
    try {
      const { data } = await fetchTable({
        variables: { params: { ids: [tableId] } },
      });
      if (data?.table?.success && data.table.data && data.table.data.length > 0) {
        setTableInfo(data.table.data[0]);
      }
    } catch (err: any) {
      console.error('Error loading table info:', err);
    }
  };

  const loadColumns = async () => {
    if (!tableId) return;
    try {
      dispatch(setColumnsLoading(true));
      const { data } = await fetchColumns({
        variables: {
          params: {
            tableId,
            search: colSearch || null,
            page: colPagination.page,
            pageSize: colPagination.pageSize,
          },
        },
      });
      if (data?.columns?.success && data.columns.data) {
        dispatch(setColumns(data.columns.data));
      } else {
        dispatch(setColumnsError(data?.columns?.message || 'Error al cargar columnas'));
      }
    } catch (err: any) {
      dispatch(setColumnsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setColumnsLoading(false));
    }
  };

  const loadRelationships = async () => {
    if (!tableId) return;
    try {
      dispatch(setRelLoading(true));
      const { data } = await fetchRelationships({
        variables: {
          params: {
            sourceTableId: tableId,
            search: relSearch || null,
            page: relPagination.page,
            pageSize: relPagination.pageSize,
          },
        },
      });
      if (data?.tableRelationships?.success && data.tableRelationships.data) {
        dispatch(setRelationships(data.tableRelationships.data));
      } else {
        dispatch(setRelError(data?.tableRelationships?.message || 'Error al cargar relaciones'));
      }
    } catch (err: any) {
      dispatch(setRelError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setRelLoading(false));
    }
  };

  const loadAvailableTables = async () => {
    try {
      setLoadingTables(true);
      const { data } = await fetchTablesList({
        variables: {
          params: {
            schemaId: null,
            search: null,
            page: 1,
            pageSize: 100,
          },
        },
      });
      if (data?.tables?.success && data.tables.data) {
        setAvailableTables(data.tables.data.tables);
      }
    } catch (err: any) {
      console.error('Error loading tables:', err);
    } finally {
      setLoadingTables(false);
    }
  };

  // === Columns handlers ===
  const handleColSearch = () => {
    dispatch(setColumnsSearch(colSearchInput));
  };

  const handleColSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleColSearch();
  };

  const handleColPaginationChange = (model: GridPaginationModel) => {
    dispatch(setColumnsPage(model.page + 1));
    dispatch(setColumnsPageSize(model.pageSize));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewColumn = async (id: string) => {
    try {
      dispatch(setColumnsLoading(true));
      const { data } = await fetchColumn({ variables: { params: { ids: [id] } } });
      if (data?.column?.success && data.column.data && data.column.data.length > 0) {
        dispatch(setSelectedColumn(data.column.data[0]));
        setViewColModalOpen(true);
      } else {
        dispatch(setColumnsError(data?.column?.message || 'Error al cargar columna'));
      }
    } catch (err: any) {
      dispatch(setColumnsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setColumnsLoading(false));
    }
  };

  const handleEditColumn = async (id: string) => {
    try {
      dispatch(setColumnsLoading(true));
      const { data } = await fetchColumn({ variables: { params: { ids: [id] } } });
      if (data?.column?.success && data.column.data && data.column.data.length > 0) {
        dispatch(setSelectedColumn(data.column.data[0]));
        setColModalMode('edit');
        setCreateEditColModalOpen(true);
      } else {
        dispatch(setColumnsError(data?.column?.message || 'Error al cargar columna'));
      }
    } catch (err: any) {
      dispatch(setColumnsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setColumnsLoading(false));
    }
  };

  const handleCreateColumn = () => {
    dispatch(clearSelectedColumn());
    setColModalMode('create');
    setColSaveError(null);
    setCreateEditColModalOpen(true);
  };

  const handleSaveColumn = async (formData: ColumnFormData) => {
    try {
      setColSaving(true);
      setColSaveError(null);

      if (colModalMode === 'create') {
        const { data: responseData } = await createColumnMutation({
          variables: {
            params: {
              tableId: formData.tableId,
              columnName: formData.columnName,
              dataType: formData.dataType,
              description: formData.description,
              businessRules: formData.businessRules,
              exampleValues: formData.exampleValues,
              isNullable: formData.isNullable,
              isPrimaryKey: formData.isPrimaryKey,
              isForeignKey: formData.isForeignKey,
              foreignKeyReference: formData.foreignKeyReference,
              keywords: formData.keywords,
              isCore: formData.isCore,
              alwaysInclude: formData.alwaysInclude,
              allowedValues: formData.allowedValues,
            },
          },
        });

        if (responseData?.createColumn?.success) {
          if (responseData.createColumn.data) {
            dispatch(addColumn(responseData.createColumn.data));
          }
          setCreateEditColModalOpen(false);
          dispatch(clearSelectedColumn());
          setColSaveError(null);
          setSuccessMessage('Columna creada exitosamente');
        } else {
          const msg = responseData?.createColumn?.message || 'Error al crear columna';
          const errs = responseData?.createColumn?.errors || [];
          setColSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        if (!formData.id) {
          setColSaveError('ID de columna no disponible');
          return;
        }
        const { data: responseData } = await updateColumnMutation({
          variables: {
            params: {
              id: formData.id,
              tableId: formData.tableId,
              columnName: formData.columnName,
              dataType: formData.dataType,
              description: formData.description,
              businessRules: formData.businessRules,
              exampleValues: formData.exampleValues,
              isNullable: formData.isNullable,
              isPrimaryKey: formData.isPrimaryKey,
              isForeignKey: formData.isForeignKey,
              foreignKeyReference: formData.foreignKeyReference,
              keywords: formData.keywords,
              isCore: formData.isCore,
              alwaysInclude: formData.alwaysInclude,
              allowedValues: formData.allowedValues,
            },
          },
        });

        if (responseData?.updateColumn?.success) {
          if (responseData.updateColumn.data) {
            dispatch(updateColumnAction(responseData.updateColumn.data));
          }
          setCreateEditColModalOpen(false);
          dispatch(clearSelectedColumn());
          setColSaveError(null);
          setSuccessMessage('Columna actualizada exitosamente');
        } else {
          const msg = responseData?.updateColumn?.message || 'Error al actualizar columna';
          const errs = responseData?.updateColumn?.errors || [];
          setColSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setColSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setColSaving(false);
    }
  };

  const handleCloseViewColModal = () => {
    setViewColModalOpen(false);
    dispatch(clearSelectedColumn());
  };

  const handleCloseCreateEditColModal = () => {
    setCreateEditColModalOpen(false);
    dispatch(clearSelectedColumn());
    setColSaveError(null);
    setColSaving(false);
  };

  const handleDeleteColClick = (column: Column) => {
    setColumnToDelete(column);
    setConfirmDeleteColOpen(true);
    setColDeleteError(null);
  };

  const handleCancelDeleteCol = () => {
    setConfirmDeleteColOpen(false);
    setColumnToDelete(null);
    setColDeleteError(null);
  };

  const handleConfirmDeleteCol = async () => {
    if (!columnToDelete) return;
    try {
      setColDeleting(true);
      setColDeleteError(null);
      const { data: responseData } = await deleteColumnMutation({
        variables: { params: { ids: [columnToDelete.id] } },
      });
      if (responseData?.deleteColumn?.success) {
        dispatch(deleteColumnAction(columnToDelete.id));
        setConfirmDeleteColOpen(false);
        setColumnToDelete(null);
        setSuccessMessage('Columna eliminada exitosamente');
      } else {
        const msg = responseData?.deleteColumn?.message || 'Error al eliminar columna';
        const errs = responseData?.deleteColumn?.errors || [];
        setColDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setColDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setColDeleting(false);
    }
  };

  // Embedding helpers
  const getSelectedColumnIds = (): string[] => {
    if (selectedRowIds.type === 'include') {
      return Array.from(selectedRowIds.ids).map((id) => String(id));
    }
    return columns.filter((col) => !selectedRowIds.ids.has(col.id)).map((col) => col.id);
  };

  const selectedCount = selectedRowIds.type === 'include'
    ? selectedRowIds.ids.size
    : columns.length - selectedRowIds.ids.size;

  const handleEmbeddingClick = () => {
    setConfirmEmbeddingOpen(true);
    setEmbeddingError(null);
  };

  const handleCancelEmbedding = () => {
    setConfirmEmbeddingOpen(false);
    setEmbeddingError(null);
  };

  const handleConfirmEmbedding = async () => {
    if (!tableId || selectedCount === 0) return;
    try {
      setGeneratingEmbedding(true);
      setEmbeddingError(null);
      const columnIds = getSelectedColumnIds();
      const { data: responseData } = await createColumnEmbeddingMutation({
        variables: { params: { tableId, columnIds } },
      });
      if (responseData?.createColumnEmbedding?.success) {
        setConfirmEmbeddingOpen(false);
        setSelectedRowIds({ type: 'include', ids: new Set<GridRowId>() });
        setSuccessMessage('Embeddings generados exitosamente');
      } else {
        const msg = responseData?.createColumnEmbedding?.message || 'Error al generar embeddings';
        const errs = responseData?.createColumnEmbedding?.errors || [];
        setEmbeddingError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setEmbeddingError(err.message || 'Error de conexión con el servidor');
    } finally {
      setGeneratingEmbedding(false);
    }
  };

  // === Relationships handlers ===
  const handleRelSearch = () => {
    dispatch(setRelSearch(relSearchInput));
  };

  const handleRelSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRelSearch();
  };

  const handleRelPaginationChange = (model: GridPaginationModel) => {
    dispatch(setRelPage(model.page + 1));
    dispatch(setRelPageSize(model.pageSize));
  };

  const handleViewRelationship = async (id: string) => {
    try {
      dispatch(setRelLoading(true));
      const { data } = await fetchRelationship({ variables: { params: { ids: [id] } } });
      if (data?.tableRelationship?.success && data.tableRelationship.data && data.tableRelationship.data.length > 0) {
        dispatch(setSelectedRelationship(data.tableRelationship.data[0]));
        setViewRelModalOpen(true);
      } else {
        dispatch(setRelError(data?.tableRelationship?.message || 'Error al cargar relación'));
      }
    } catch (err: any) {
      dispatch(setRelError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setRelLoading(false));
    }
  };

  const handleEditRelationship = async (id: string) => {
    try {
      dispatch(setRelLoading(true));
      // Load available tables for the combobox
      await loadAvailableTables();
      const { data } = await fetchRelationship({ variables: { params: { ids: [id] } } });
      if (data?.tableRelationship?.success && data.tableRelationship.data && data.tableRelationship.data.length > 0) {
        dispatch(setSelectedRelationship(data.tableRelationship.data[0]));
        setRelModalMode('edit');
        setCreateEditRelModalOpen(true);
      } else {
        dispatch(setRelError(data?.tableRelationship?.message || 'Error al cargar relación'));
      }
    } catch (err: any) {
      dispatch(setRelError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setRelLoading(false));
    }
  };

  const handleCreateRelationship = async () => {
    dispatch(clearSelectedRelationship());
    setRelModalMode('create');
    setRelSaveError(null);
    await loadAvailableTables();
    setCreateEditRelModalOpen(true);
  };

  const handleSaveRelationship = async (formData: RelationshipFormData) => {
    try {
      setRelSaving(true);
      setRelSaveError(null);

      if (relModalMode === 'create') {
        const { data: responseData } = await createRelationshipMutation({
          variables: {
            params: {
              sourceTableId: formData.sourceTableId,
              targetTableId: formData.targetTableId,
              joinCondition: formData.joinCondition,
              relationshipType: formData.relationshipType,
              description: formData.description,
              businessContext: formData.businessContext,
              isIdentifying: formData.isIdentifying,
            },
          },
        });

        if (responseData?.createTableRelationship?.success) {
          if (responseData.createTableRelationship.data) {
            dispatch(addRelationshipAction(responseData.createTableRelationship.data));
          }
          setCreateEditRelModalOpen(false);
          dispatch(clearSelectedRelationship());
          setRelSaveError(null);
          setSuccessMessage('Relación creada exitosamente');
        } else {
          const msg = responseData?.createTableRelationship?.message || 'Error al crear relación';
          const errs = responseData?.createTableRelationship?.errors || [];
          setRelSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        if (!formData.id) {
          setRelSaveError('ID de relación no disponible');
          return;
        }

        const { data: responseData } = await updateRelationshipMutation({
          variables: {
            params: {
              id: formData.id,
              sourceTableId: formData.sourceTableId,
              targetTableId: formData.targetTableId,
              joinCondition: formData.joinCondition,
              relationshipType: formData.relationshipType,
              description: formData.description,
              businessContext: formData.businessContext,
              isIdentifying: formData.isIdentifying,
            },
          },
        });

        if (responseData?.updateTableRelationship?.success) {
          if (responseData.updateTableRelationship.data) {
            const targetTable = availableTables.find(
              (t) => t.id === formData.targetTableId
            );
            const updatedRelationship = {
              ...responseData.updateTableRelationship.data,
              targetTable: targetTable || null,
            } as Relationship;
            dispatch(updateRelationshipAction(updatedRelationship));
          }
          setCreateEditRelModalOpen(false);
          dispatch(clearSelectedRelationship());
          setRelSaveError(null);
          setSuccessMessage('Relación actualizada exitosamente');
        } else {
          const msg = responseData?.updateTableRelationship?.message || 'Error al actualizar relación';
          const errs = responseData?.updateTableRelationship?.errors || [];
          setRelSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setRelSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setRelSaving(false);
    }
  };

  const handleCloseViewRelModal = () => {
    setViewRelModalOpen(false);
    dispatch(clearSelectedRelationship());
  };

  const handleCloseEditRelModal = () => {
    setCreateEditRelModalOpen(false);
    dispatch(clearSelectedRelationship());
    setRelSaveError(null);
    setRelSaving(false);
  };

  const handleDeleteRelClick = (relationship: Relationship) => {
    setRelationshipToDelete(relationship);
    setConfirmDeleteRelOpen(true);
    setRelDeleteError(null);
  };

  const handleCancelDeleteRel = () => {
    setConfirmDeleteRelOpen(false);
    setRelationshipToDelete(null);
    setRelDeleteError(null);
  };

  const handleConfirmDeleteRel = async () => {
    if (!relationshipToDelete) return;
    try {
      setRelDeleting(true);
      setRelDeleteError(null);
      const { data: responseData } = await deleteRelationshipMutation({
        variables: { params: { ids: [relationshipToDelete.id] } },
      });
      if (responseData?.deleteTableRelationship?.success) {
        dispatch(deleteRelationshipAction(relationshipToDelete.id));
        setConfirmDeleteRelOpen(false);
        setRelationshipToDelete(null);
        setSuccessMessage('Relación eliminada exitosamente');
      } else {
        const msg = responseData?.deleteTableRelationship?.message || 'Error al eliminar relación';
        const errs = responseData?.deleteTableRelationship?.errors || [];
        setRelDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setRelDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setRelDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  // === Grid column definitions ===
  const gridColumns: GridColDef<Column>[] = [
    { field: 'columnName', headerName: 'Nombre de Columna', flex: 1, minWidth: 150 },
    { field: 'dataType', headerName: 'Tipo de Dato', width: 120 },
    { field: 'description', headerName: 'Descripción', flex: 2, minWidth: 200 },
    { field: 'isPrimaryKey', headerName: 'PK', width: 70, valueGetter: (value, row) => (row.isPrimaryKey ? 'Sí' : 'No') },
    { field: 'isForeignKey', headerName: 'FK', width: 70, valueGetter: (value, row) => (row.isForeignKey ? 'Sí' : 'No') },
    { field: 'isNullable', headerName: 'Nullable', width: 80, valueGetter: (value, row) => (row.isNullable ? 'Sí' : 'No') },
    { field: 'isCore', headerName: 'Core', width: 70, valueGetter: (value, row) => (row.isCore ? 'Sí' : 'No') },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleViewColumn(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEditColumn(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteColClick(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const relGridColumns: GridColDef<Relationship>[] = [
    {
      field: 'targetTableName',
      headerName: 'Tabla Destino',
      flex: 1,
      minWidth: 180,
      valueGetter: (value, row) =>
        row.targetTable ? `${row.targetTable.schemaName}.${row.targetTable.tableName}` : row.targetTableId,
    },
    { field: 'description', headerName: 'Descripción', flex: 2, minWidth: 200 },
    {
      field: 'targetPrimaryKeyColumns',
      headerName: 'Columnas PK',
      width: 150,
      valueGetter: (value, row) =>
        row.targetTable ? row.targetTable.primaryKeyColumns.join(', ') : '',
    },
    {
      field: 'targetKeywords',
      headerName: 'Palabras Clave',
      width: 150,
      valueGetter: (value, row) =>
        row.targetTable ? row.targetTable.keywords.join(', ') : '',
    },
    { field: 'relationshipType', headerName: 'Tipo', width: 120 },
    { field: 'joinCondition', headerName: 'Condición Join', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleViewRelationship(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEditRelationship(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeleteRelClick(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="table-config-container">
        <Paper elevation={2} className="table-config-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/tables')}>
              Tablas
            </Link>
            <Typography color="text.primary">Configuración de Tabla</Typography>
          </Breadcrumbs>

          <Box className="table-config-header">
            <Typography variant="h4" component="h1">
              Configuración de Tabla
            </Typography>
            {tableInfo && (
              <Typography variant="body2" color="text.secondary">
                {tableInfo.schemaName}.{tableInfo.tableName}
              </Typography>
            )}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Columnas" id="table-config-tab-0" aria-controls="table-config-tabpanel-0" />
              <Tab label="Relaciones" id="table-config-tab-1" aria-controls="table-config-tabpanel-1" />
            </Tabs>
          </Box>

          {/* ==================== COLUMNAS TAB ==================== */}
          <TabPanel value={tabValue} index={0}>
            <Box className="table-config-toolbar">
              <Box className="search-box">
                <TextField
                  size="small"
                  placeholder="Buscar columna..."
                  value={colSearchInput}
                  onChange={(e) => setColSearchInput(e.target.value)}
                  onKeyPress={handleColSearchKeyPress}
                  sx={{ minWidth: 250 }}
                />
                <Button variant="contained" startIcon={<Search />} onClick={handleColSearch} sx={{ ml: 1 }}>
                  Buscar
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<EmbeddingIcon />}
                  onClick={handleEmbeddingClick}
                  disabled={selectedCount === 0}
                >
                  Generar Embedding ({selectedCount})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateColumn}
                  sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
                >
                  Nueva Columna
                </Button>
              </Box>
            </Box>

            {colError && (
              <Box className="error-box">
                <Typography color="error">{colError}</Typography>
              </Box>
            )}

            <Box className="data-grid-container">
              <DataGrid
                rows={columns}
                columns={gridColumns}
                loading={colLoading || fetchingColList}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={{ page: colPagination.page - 1, pageSize: colPagination.pageSize }}
                onPaginationModelChange={handleColPaginationChange}
                paginationMode="server"
                rowCount={colPagination.totalRecords}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectedRowIds}
                onRowSelectionModelChange={(newSelection) => setSelectedRowIds(newSelection)}
                autoHeight
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              />
            </Box>
          </TabPanel>

          {/* ==================== RELACIONES TAB ==================== */}
          <TabPanel value={tabValue} index={1}>
            <Box className="table-config-toolbar">
              <Box className="search-box">
                <TextField
                  size="small"
                  placeholder="Buscar relación..."
                  value={relSearchInput}
                  onChange={(e) => setRelSearchInput(e.target.value)}
                  onKeyPress={handleRelSearchKeyPress}
                  sx={{ minWidth: 250 }}
                />
                <Button variant="contained" startIcon={<Search />} onClick={handleRelSearch} sx={{ ml: 1 }}>
                  Buscar
                </Button>
              </Box>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateRelationship}
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
              >
                Nueva Relación
              </Button>
            </Box>

            {relError && (
              <Box className="error-box">
                <Typography color="error">{relError}</Typography>
              </Box>
            )}

            <Box className="data-grid-container">
              <DataGrid
                rows={relationships}
                columns={relGridColumns}
                loading={relLoading || fetchingRelList}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={{ page: relPagination.page - 1, pageSize: relPagination.pageSize }}
                onPaginationModelChange={handleRelPaginationChange}
                paginationMode="server"
                rowCount={relPagination.totalRecords}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              />
            </Box>
          </TabPanel>
        </Paper>

        {/* Column Modals */}
        <ViewColumnModal open={viewColModalOpen} onClose={handleCloseViewColModal} />
        <CreateEditColumnModal
          open={createEditColModalOpen}
          onClose={handleCloseCreateEditColModal}
          onSave={handleSaveColumn}
          mode={colModalMode}
          saving={colSaving}
          saveError={colSaveError}
          tableId={tableId || ''}
        />
        <ConfirmDeleteColumnDialog
          open={confirmDeleteColOpen}
          onClose={handleCancelDeleteCol}
          onConfirm={handleConfirmDeleteCol}
          columnName={columnToDelete?.columnName || ''}
          deleting={colDeleting}
        />
        <ConfirmCreateColumnEmbeddingDialog
          open={confirmEmbeddingOpen}
          onClose={handleCancelEmbedding}
          onConfirm={handleConfirmEmbedding}
          selectedCount={selectedCount}
          generating={generatingEmbedding}
        />

        {/* Relationship Modals */}
        <ViewRelationshipModal open={viewRelModalOpen} onClose={handleCloseViewRelModal} />
        <CreateEditRelationshipModal
          open={createEditRelModalOpen}
          onClose={handleCloseEditRelModal}
          onSave={handleSaveRelationship}
          mode={relModalMode}
          saving={relSaving}
          saveError={relSaveError}
          sourceTableId={tableId || ''}
          tables={availableTables}
          loadingTables={loadingTables}
        />
        <ConfirmDeleteRelationshipDialog
          open={confirmDeleteRelOpen}
          onClose={handleCancelDeleteRel}
          onConfirm={handleConfirmDeleteRel}
          relationshipDescription={
            relationshipToDelete?.targetTable
              ? `${relationshipToDelete.targetTable.schemaName}.${relationshipToDelete.targetTable.tableName}`
              : relationshipToDelete?.description || ''
          }
          deleting={relDeleting}
        />

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Error Snackbars */}
        {colDeleteError && (
          <Snackbar open={!!colDeleteError} autoHideDuration={6000} onClose={() => setColDeleteError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Alert onClose={() => setColDeleteError(null)} severity="error" sx={{ width: '100%' }}>{colDeleteError}</Alert>
          </Snackbar>
        )}
        {embeddingError && (
          <Snackbar open={!!embeddingError} autoHideDuration={6000} onClose={() => setEmbeddingError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Alert onClose={() => setEmbeddingError(null)} severity="error" sx={{ width: '100%' }}>{embeddingError}</Alert>
          </Snackbar>
        )}
        {relDeleteError && (
          <Snackbar open={!!relDeleteError} autoHideDuration={6000} onClose={() => setRelDeleteError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Alert onClose={() => setRelDeleteError(null)} severity="error" sx={{ width: '100%' }}>{relDeleteError}</Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default TableConfiguration;
